// Cart-drawer inline matching-set suggester.
// Reads the [data-drawer-suggester] JSON island rendered inside the drawer's
// morph region (snippets/skullette-drawer-suggester-data.liquid) and builds a
// chip + candidate block directly UNDER each matching cart line (<tr
// data-key>). Loaded ONCE from theme.liquid - a <script src> swapped in by a
// drawer morph would never execute, so re-init runs off a MutationObserver.
//
// Re-render discipline (lesson: the first build re-rendered on EVERY body
// mutation - popup apps and animations mutate constantly, so the blocks were
// torn down mid-click and the chips felt dead): we only rebuild when the
// island CONTENT changes (cart changed server-side) or when a morph wiped the
// injected rows. Chip toggles mutate the existing DOM and persist through
// rebuilds via openKeyOverride.
//
// Adds go through ONE /cart/add.js call carrying _attach_source =
// 'drawer-suggest' (+ preorder promise props when the candidate is a
// pre-order piece) and refresh the drawer through Horizon's own
// CartLinesUpdateEvent contract; sessionStorage hands the open block to the
// next line after each add.
import { CartLinesUpdateEvent } from '@shopify/events';

const NEXT_KEY = 'sk-sugg-next';
const SECTION_ID = 'cart-drawer-section';

let lastSig = null;
let openKeyOverride; // undefined = no user toggle yet this render generation

function currentIsland() {
  return document.querySelector('cart-drawer-component [data-drawer-suggester]');
}

function setOpen(td, open) {
  const body = td.querySelector('.sk-sugg__body');
  const chip = td.querySelector('.sk-sugg__chip');
  if (!body || !chip) return;
  body.hidden = !open;
  td.querySelector('.sk-sugg')?.classList.toggle('is-open', open);
  chip.setAttribute('aria-expanded', String(open));
  const caret = td.querySelector('.sk-sugg__caret');
  if (caret) caret.textContent = open ? '▾' : '▸';
}

function render() {
  const island = currentIsland();
  document.querySelectorAll('.sk-sugg-row').forEach((el) => el.remove());
  if (!island) return;

  let plan;
  try {
    plan = JSON.parse(island.textContent);
  } catch {
    return;
  }
  const anchors = plan?.anchors ?? [];
  if (!anchors.length) return;

  const preferred = openKeyOverride !== undefined ? openKeyOverride : sessionStorage.getItem(NEXT_KEY);
  const openKey = anchors.some((a) => a.key === preferred) ? preferred : anchors[0]?.key;

  for (const anchor of anchors) {
    const lineRow = document.querySelector(`cart-drawer-component tr[data-key="${CSS.escape(anchor.key)}"]`);
    if (!lineRow) continue;

    const cols = lineRow.children.length || 4;
    const tr = document.createElement('tr');
    tr.className = 'sk-sugg-row';
    const td = document.createElement('td');
    td.colSpan = cols;

    const open = anchor.key === openKey;
    td.innerHTML = `
      <div class="sk-sugg${open ? ' is-open' : ''}" data-sugg-key="${anchor.key}">
        <button type="button" class="sk-sugg__chip" aria-expanded="${open}">
          ${anchor.label} <span class="sk-sugg__caret" aria-hidden="true">${open ? '▾' : '▸'}</span>
        </button>
        <div class="sk-sugg__body"${open ? '' : ' hidden'}>
          ${anchor.cands
            .map(
              (c, i) => `
            <div class="sk-sugg__cand" data-sugg-idx="${i}">
              ${c.img ? `<img class="sk-sugg__thumb" src="${c.img}" alt="${c.display}" width="44" height="58" loading="lazy">` : ''}
              <span class="sk-sugg__copy"><b>${c.display}</b><span>${c.line}${
                // pre-order candidate: the promise shows BEFORE the add, not
                // after - window verbatim from the island ("Ships Aug 26 – Sep 2")
                c.preB ? ` · ${c.preE ? c.preE.replace(/^Ships\s+/, 'ships ') : 'pre-order'}` : ''
              }</span></span>
              <span class="sk-sugg__price">${c.pay}</span>
              <button type="button" class="sk-sugg__add">Add</button>
            </div>`
            )
            .join('')}
        </div>
      </div>`;
    tr.appendChild(td);
    lineRow.after(tr);

    td.querySelector('.sk-sugg__chip').addEventListener('click', () => {
      const willOpen = td.querySelector('.sk-sugg__body').hidden;
      // close the others so only one block is open at a time
      document.querySelectorAll('.sk-sugg-row td').forEach((other) => {
        if (other !== td) setOpen(other, false);
      });
      setOpen(td, willOpen);
      openKeyOverride = willOpen ? anchor.key : null;
    });

    td.querySelectorAll('.sk-sugg__cand').forEach((row) => {
      const idx = parseInt(row.getAttribute('data-sugg-idx'), 10);
      const cand = anchor.cands[idx];
      const addBtn = row.querySelector('.sk-sugg__add');
      addBtn.addEventListener('click', async () => {
        if (addBtn.disabled) return;
        addBtn.disabled = true;
        addBtn.textContent = '…';
        try {
          // hand the open block to the next line after the morph re-render
          const next = anchors[(anchors.indexOf(anchor) + 1) % anchors.length];
          sessionStorage.setItem(NEXT_KEY, next?.key ?? anchor.key);
          openKeyOverride = undefined;

          const properties = { _attach_source: 'drawer-suggest' };
          if (cand.preB) {
            properties._preorder = 'true';
            properties._preorder_batch = cand.preB;
            if (cand.preE) properties._preorder_eta = cand.preE;
          }
          const deferred = CartLinesUpdateEvent.createPromise();
          document.dispatchEvent(
            new CartLinesUpdateEvent({
              action: 'add',
              context: 'sk-drawer-suggest',
              lines: [{ id: String(cand.vid), quantity: 1 }],
              promise: deferred.promise,
            })
          );
          const res = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{ id: cand.vid, quantity: 1, properties }],
              sections: SECTION_ID,
              sections_url: window.location.pathname,
            }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data?.description || `cart/add ${res.status}`);
          addBtn.textContent = 'Added ✓';
          deferred.resolve({ detail: { sections: data.sections } });
          // island content changes with the morph; the observer rebuilds
        } catch (error) {
          console.error('drawer suggester add failed', error);
          addBtn.disabled = false;
          addBtn.textContent = 'Add';
        }
      });
    });
  }
}

let queued = false;
function reconcile() {
  // Rebuild ONLY when the cart actually changed (island content differs) or a
  // morph wiped the injected rows - never on unrelated page mutations.
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    const island = currentIsland();
    const sig = island ? island.textContent : null;
    const rowsPresent = !!document.querySelector('.sk-sugg-row');
    if (sig !== lastSig || (sig && !rowsPresent)) {
      if (sig !== lastSig) openKeyOverride = undefined; // fresh cart state
      lastSig = sig;
      render();
    }
  });
}

new MutationObserver(reconcile).observe(document.body, { childList: true, subtree: true });
reconcile();
